import { createElement as rc } from 'react';
import { Icon, View, fromTheme, styled } from 'lib_ui-primitives';
import PropTypes from 'prop-types';
const NoIcon = styled(View).attrs({ name: 'no-icon-offset' })`
    margin-left: ${fromTheme('blocklyEditor', 'accessModifier', 'noIconSize')};
`;
const WhiteIcon = styled(Icon).attrs(({ theme }) => ({
    fontColor: theme.blocklyEditor.accessModifier.white,
    iconSize: theme.blocklyEditor.accessModifier.size
}))`
    margin-top: ${fromTheme('blocklyEditor', 'accessModifier', 'margin')};
`;
const GreenIcon = styled(Icon).attrs(({ theme }) => ({
    fontColor: theme.blocklyEditor.accessModifier.green,
    iconSize: theme.blocklyEditor.accessModifier.size
}))`
    margin-top: ${fromTheme('blocklyEditor', 'accessModifier', 'margin')};
`;
const RedIcon = styled(Icon).attrs(({ theme }) => ({
    fontColor: theme.blocklyEditor.accessModifier.red,
    iconSize: theme.blocklyEditor.accessModifier.size
}))`
    margin-top: ${fromTheme('blocklyEditor', 'accessModifier', 'margin')};
`;
/**
 * These properties have been renamed, and should not longer be passed in.
 * but leaving them in for backwards compatibility.
 * @typedef {object} CompatibilityProps
 * @property {Array} [removeForFeatureFlags]
 * @property {Array} [addForFeatureFlags]
 *
 * @typedef {object} ChildNodes
 * @property {Array<ChildNodes>} [children]
 *
 * These props are NOT yet passed in, but may be in the future.
 * And it's easier to understand this logic if we include them already
 * @typedef {object} futureProps
 * @property {Array} [hideForViewPort]
 *
 * @typedef {CompatibilityProps & ChildNodes & futureProps} OtherProps
 */
/**
 *
 * @param {object} props
 * @param {boolean} [props.isCollapsed]
 * @param {function} [props.toggleIsCollapsed]
 * @param {import('@sstdev/types').AccessModifiersOnBlockly & OtherProps} props.data
 * @returns
 */
function AccessModifiersAsIcons(props) {
    const {
        isCollapsed = true,
        toggleIsCollapsed,
        data: {
            children,
            removeForFeatureFlag,
            removeForFeatureFlags: _removeForFeatureFlags,
            addForFeatureFlag,
            addForFeatureFlags: _addForFeatureFlags,
            showForTenantSetting,
            hideForTenantSetting,
            showForViewPort,
            hideForViewPort,
            hiddenWhen,
            hiddenFor
        }
    } = props;
    const icons = [];
    const removeForFeatureFlags = removeForFeatureFlag || _removeForFeatureFlags;
    const addForFeatureFlags = addForFeatureFlag || _addForFeatureFlags;

    if (hiddenWhen?.some(({ hidewhen }) => hidewhen === 'ONLINE')) {
        //ONLINE/OFFLINE
        //only show when offline
        icons.push(rc(RedIcon, { key: 'network', 'data-testid': 'network-red' }, 'signal_cellular_nodata'));
    } else if (hiddenWhen?.some(({ hidewhen }) => hidewhen === 'OFFLINE')) {
        icons.push(rc(GreenIcon, { key: 'network', 'data-testid': 'network-green' }, 'signal_cellular_alt'));
    } else {
        icons.push(rc(NoIcon, { key: 'network', 'data-testid': 'network-none' }));
    }

    //feature flag
    if (removeForFeatureFlags?.length && addForFeatureFlags?.length) {
        //some FFs are configured for to add this page
        //and others to remove it, show a white icon
        icons.push(rc(WhiteIcon, { key: 'ff', 'data-testid': 'ff-white' }, 'flag'));
    } else if (removeForFeatureFlags?.length) {
        icons.push(rc(RedIcon, { key: 'ff', 'data-testid': 'ff-red' }, 'flag'));
    } else if (addForFeatureFlags?.length) {
        icons.push(rc(GreenIcon, { key: 'ff', 'data-testid': 'ff-green' }, 'flag'));
    }
    //tenant Settings
    else if (hideForTenantSetting?.length && showForTenantSetting?.length) {
        //some TSs are configured for to add this page
        //and others to remove it, show a white icon
        icons.push(rc(WhiteIcon, { key: 'ff', 'data-testid': 'ts-white' }, 'settings'));
    } else if (hideForTenantSetting?.length) {
        icons.push(rc(RedIcon, { key: 'ff', 'data-testid': 'ts-red' }, 'settings'));
    } else if (showForTenantSetting?.length) {
        icons.push(rc(GreenIcon, { key: 'ff', 'data-testid': 'ts-green' }, 'settings'));
    } else {
        icons.push(rc(NoIcon, { key: 'ff' }));
    }

    icons.push(rc(NoIcon, { style: { marginLeft: '6px' }, key: 'spacer1' }));

    //ViewPort:
    //MOBILE
    //if no show for, and this viewport is not explicitly configured as hidden,
    //OR if explicitly configured to show for this viewport: green
    if (
        (!showForViewPort?.length && !hideForViewPort?.some(({ hideforviewport }) => hideforviewport === 'MOBILE')) ||
        showForViewPort?.some(({ showforviewport }) => showforviewport === 'MOBILE')
    ) {
        icons.push(rc(GreenIcon, { key: 'mobile', 'data-testid': 'mobile-green' }, 'smartphone'));
    } else if (
        //if this page is explicitly for some other viewport (but not this one,
        //as that would have been handled in the previous branch)
        //Or if this page is explicitly hidden for this viewport: red
        showForViewPort?.length ||
        hideForViewPort?.some(({ hideforviewport }) => hideforviewport === 'MOBILE')
    ) {
        icons.push(rc(RedIcon, { key: 'mobile', 'data-testid': 'mobile-red' }, 'mobile_off'));
    } else {
        icons.push(rc(NoIcon, { key: 'mobile', 'data-testid': 'no-mobile' }));
    }

    //TABLET
    if (
        (!showForViewPort?.length && !hideForViewPort?.some(({ hideforviewport }) => hideforviewport === 'TABLET')) ||
        showForViewPort?.some(({ showforviewport }) => showforviewport === 'TABLET')
    ) {
        icons.push(rc(GreenIcon, { key: 'tablet', 'data-testid': 'tablet-green' }, 'tablet'));
    } else if (
        showForViewPort?.length ||
        hideForViewPort?.some(({ hideforviewport }) => hideforviewport === 'TABLET')
    ) {
        icons.push(rc(RedIcon, { key: 'tablet', 'data-testid': 'tablet-red' }, 'tablet'));
    } else {
        icons.push(rc(NoIcon, { key: 'tablet', 'data-testid': 'no-tablet' }));
    }

    //DESKTOP
    if (
        (!showForViewPort?.length && !hideForViewPort?.some(({ hideforviewport }) => hideforviewport === 'DESKTOP')) ||
        showForViewPort?.some(({ showforviewport }) => showforviewport === 'DESKTOP')
    ) {
        icons.push(rc(GreenIcon, { key: 'desktop', 'data-testid': 'desktop-green' }, 'desktop_windows'));
    } else if (
        showForViewPort?.length ||
        hideForViewPort?.some(({ hideforviewport }) => hideforviewport === 'DESKTOP')
    ) {
        icons.push(rc(RedIcon, { key: 'desktop', 'data-testid': 'desktop-red' }, 'desktop_access_disabled'));
    } else {
        icons.push(rc(NoIcon, { key: 'desktop', 'data-testid': 'no-desktop' }));
    }

    icons.push(rc(NoIcon, { style: { marginLeft: '6px' }, key: 'spacer2' }));

    //user Access
    //USER
    if (hiddenFor?.some(({ hiddenfor }) => hiddenfor === 'USER')) {
        icons.push(rc(RedIcon, { key: 'user', 'data-testid': 'user-red' }, 'groups'));
    } else {
        icons.push(rc(GreenIcon, { key: 'user', 'data-testid': 'user-green' }, 'groups'));
    }
    //ADMIN
    if (hiddenFor?.some(({ hiddenfor }) => hiddenfor === 'ADMIN')) {
        icons.push(rc(RedIcon, { key: 'admin', 'data-testid': 'admin-red' }, 'supervisor_account'));
    } else {
        icons.push(rc(GreenIcon, { key: 'admin', 'data-testid': 'admin-green' }, 'supervisor_account'));
    }
    //SUPPORT
    if (hiddenFor?.some(({ hiddenfor }) => hiddenfor === 'SUPPORT')) {
        icons.push(rc(RedIcon, { key: 'support', 'data-testid': 'support-red' }, 'person'));
    } else {
        icons.push(rc(GreenIcon, { key: 'support', 'data-testid': 'support-green' }, 'person'));
    }

    if (children) {
        icons.push(
            rc(Icon, { key: 'expand', onClick: toggleIsCollapsed }, isCollapsed ? 'expand_more' : 'expand_less')
        );
    } else {
        icons.push(rc(NoIcon, { style: { marginLeft: '20px' }, key: 'spacer3' }));
    }

    if (!icons.length) return null;
    return rc(View, null, ...icons);
}

AccessModifiersAsIcons.propTypes = {
    id: PropTypes.string,
    isCollapsed: PropTypes.bool,
    toggleIsCollapsed: PropTypes.func,
    data: PropTypes.shape({
        children: PropTypes.array,
        removeForFeatureFlag: PropTypes.array,
        removeForFeatureFlags: PropTypes.array,
        addForFeatureFlag: PropTypes.array,
        addForFeatureFlags: PropTypes.array,
        showForViewPort: PropTypes.array,
        hideForViewPort: PropTypes.array,
        showForTenantSetting: PropTypes.array,
        hideForTenantSetting: PropTypes.array,
        hiddenWhen: PropTypes.array,
        hiddenFor: PropTypes.array,
        isTabContainer: PropTypes.bool
    })
};

export default AccessModifiersAsIcons;
